import { useTranslation } from "react-i18next";

import type { PatientSearchOutcome } from "@thalamos/aspire-common";

import { Banner, BannerList } from "~/components/design-system/index.js";

export type PatientSearchResultBannerProps = {
  outcome: PatientSearchOutcome;
  isPatientMerge?: boolean;
  canSearchByPdsDemographics: boolean;
  criteriaType: "nhs-number" | "demographics" | "pds-demographics";
  setCriteriaType: (
    criteriaType: "nhs-number" | "demographics" | "pds-demographics",
  ) => void;
  continueInstruction?: string;
  alternativeInstruction?: string;
};

export const PatientSearchResultBanner = ({
  outcome,
  isPatientMerge,
  canSearchByPdsDemographics,
  criteriaType,
  setCriteriaType,
  continueInstruction = " Select the correct patient and click continue. ",
  alternativeInstruction = " or create a new patient.",
}: PatientSearchResultBannerProps) => {
  const { t } = useTranslation();

  if (
    outcome === "error/no-matches" &&
    criteriaType === "demographics" &&
    canSearchByPdsDemographics
  ) {
    return (
      <Banner
        title={"No matches found"}
        bannerType={BannerList.ERROR}
        body={
          "No patients have been found with the details above. Clear the search and try again, or search the PDS by demographics."
        }
        rightButtonLabel="Search PDS"
        onRightButton={() => setCriteriaType("pds-demographics")}
      />
    );
  }

  switch (outcome) {
    case "success/unique-match":
      return isPatientMerge ? (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.uniqueMatch.title",
          )}
          bannerType={BannerList.CONFIRMATION}
          body={
            t(
              "pages.patientSearch.patientSearchResultBanner.uniqueMatch.body1",
              {
                continueInstruction,
              },
            ) +
            "\n" +
            t(
              "pages.patientSearch.patientSearchResultBanner.uniqueMatch.patientMergeBody2",
            )
          }
        />
      ) : (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.uniqueMatch.title",
          )}
          bannerType={BannerList.CONFIRMATION}
          body={
            t(
              "pages.patientSearch.patientSearchResultBanner.uniqueMatch.body1",
              {
                continueInstruction,
              },
            ) +
            "\n" +
            t(
              "pages.patientSearch.patientSearchResultBanner.uniqueMatch.body2",
              { alternativeInstruction },
            )
          }
        />
      );

    case "error/too-many-matches-pds":
      return (
        <Banner
          title={"Too many matches were found"}
          bannerType={BannerList.ERROR}
          body={
            "A unique match was not found on PDS based on your search criteria. Please narrow down your search criteria and try again."
          }
        />
      );

    case "error/too-many-matches":
      return (
        <Banner
          title={"Too many matches were found"}
          bannerType={BannerList.ERROR}
          body={
            "More than 10 results matched the criteria you specified in the eMHA Patient Index. Please narrow down your search criteria (or disable phonetic matching if it was enabled) and try again."
          }
        />
      );

    case "info/multiple-matches":
      return isPatientMerge ? (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.multipleMatches.title",
          )}
          bannerType={BannerList.WARNING}
          body={
            t(
              "pages.patientSearch.patientSearchResultBanner.multipleMatches.body1",
            ) +
            "\n" +
            t(
              "pages.patientSearch.patientSearchResultBanner.multipleMatches.patientMergeBody2",
            )
          }
        />
      ) : (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.multipleMatches.title",
          )}
          bannerType={BannerList.WARNING}
          body={
            t(
              "pages.patientSearch.patientSearchResultBanner.multipleMatches.body1",
            ) +
            "\n" +
            t(
              "pages.patientSearch.patientSearchResultBanner.multipleMatches.body2",
            )
          }
        />
      );

    case "error/no-matches":
      return isPatientMerge ? (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.noMatches.title",
          )}
          bannerType={BannerList.ERROR}
          body={t(
            "pages.patientSearch.patientSearchResultBanner.noMatches.patientMergeBody",
          )}
        />
      ) : (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.noMatches.title",
          )}
          bannerType={BannerList.ERROR}
          body={t(
            "pages.patientSearch.patientSearchResultBanner.noMatches.body",
            { alternativeInstruction },
          )}
        />
      );

    case "error/unknown":
      return (
        <Banner
          title={t(
            "pages.patientSearch.patientSearchResultBanner.unknown.title",
          )}
          bannerType={BannerList.ERROR}
          body={t("pages.patientSearch.patientSearchResultBanner.unknown.body")}
        />
      );
  }
};
